// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
export type UtmKey = (typeof UTM_KEYS)[number];
export type CampaignAttribution = Partial<Record<UtmKey, string>>;

const ATTRIBUTION_STORAGE_KEY = 'polaris.patient.funnel-attribution';
const BOOKING_MARKER_PREFIX = 'polaris.patient.appointment-booked.';
const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredAttribution extends CampaignAttribution {
  capturedAt: number;
}

export interface FunnelAnalyticsPayload extends CampaignAttribution {
  page_location: string;
  page_referrer: string;
}

function isStoredAttribution(value: unknown): value is StoredAttribution {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<StoredAttribution>;
  return typeof candidate.capturedAt === 'number';
}

function readStoredAttribution(now = Date.now()): StoredAttribution | undefined {
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredAttribution(parsed) || now - parsed.capturedAt >= ATTRIBUTION_MAX_AGE_MS) {
      window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

function getUtmValues(searchParams: URLSearchParams): CampaignAttribution {
  const attribution: CampaignAttribution = {};
  for (const key of UTM_KEYS) {
    const value = searchParams.get(key)?.trim();
    if (value) {
      attribution[key] = value;
    }
  }
  return attribution;
}

export function buildSanitizedRegisterUrl(url: URL): string {
  const sanitized = new URL('/register', url.origin);
  const attribution = getUtmValues(url.searchParams);
  for (const key of UTM_KEYS) {
    const value = attribution[key];
    if (value) {
      sanitized.searchParams.set(key, value);
    }
  }
  return sanitized.toString();
}

export function buildSanitizedOnboardingUrl(url: URL): string {
  return new URL('/onboarding', url.origin).toString();
}

export function captureRegisterAttribution(url: URL, now = Date.now()): StoredAttribution | undefined {
  const current = getUtmValues(url.searchParams);
  const existing = readStoredAttribution(now);
  const attribution = Object.keys(current).length > 0 ? current : existing;
  if (!attribution) {
    return undefined;
  }

  const stored: StoredAttribution = {
    ...attribution,
    capturedAt: existing && Object.keys(current).length === 0 ? existing.capturedAt : now,
  };
  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Analytics must never affect the intake flow when storage is unavailable.
  }
  return stored;
}

export function getFunnelPayload(url: URL): FunnelAnalyticsPayload {
  const stored = readStoredAttribution();
  const storedRegisterUrl = stored ? new URL('/register', url.origin) : undefined;
  if (storedRegisterUrl && stored) {
    for (const key of UTM_KEYS) {
      const value = stored[key];
      if (value) {
        storedRegisterUrl.searchParams.set(key, value);
      }
    }
  }
  const payload: FunnelAnalyticsPayload = {
    page_location: url.pathname === '/register' ? buildSanitizedRegisterUrl(url) : buildSanitizedOnboardingUrl(url),
    page_referrer: url.pathname === '/register' ? url.origin : (storedRegisterUrl?.toString() ?? url.origin),
  };
  if (stored) {
    for (const key of UTM_KEYS) {
      const value = stored[key];
      if (value) {
        payload[key] = value;
      }
    }
  }
  return payload;
}

function bookingMarkerKey(uniqueId: string): string {
  let hash = 2166136261;
  for (const character of uniqueId) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `${BOOKING_MARKER_PREFIX}${(hash >>> 0).toString(16)}`;
}

export function claimBookingEvent(uniqueId: string): boolean {
  try {
    const key = bookingMarkerKey(uniqueId);
    if (window.localStorage.getItem(key)) {
      return false;
    }
    window.localStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}

export function trackAppointmentBooked(uniqueId: string): void {
  try {
    if (!window.gtag || !claimBookingEvent(uniqueId)) {
      return;
    }
    window.gtag('event', 'appointment_booked', getFunnelPayload(new URL(window.location.href)));
  } catch {
    // Analytics must never block a successful appointment booking.
  }
}

export function getAttributionStorageKeyForTest(): string {
  return ATTRIBUTION_STORAGE_KEY;
}
